import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import { Mail, Phone, MapPin, User, FileText } from "lucide-react";

export default function Impressum() {
  const { t, i18n } = useTranslation();
  const { lang = 'de' } = useParams();
  const { getPath } = useLocalizedPath();

  return (
    <div data-testid="impressum-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white">
            {t('impressum.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Card className="bg-white border">
            <CardContent className="p-6 md:p-10">
              <div className="space-y-8">
                {/* Angaben gemäß § 5 DDG */}
                <div>
                  <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748] mb-4 flex items-center gap-3">
                    <FileText className="h-6 w-6 text-orange-500" />
                    {t('impressum.according')}
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-lg font-semibold text-[#2D3748] mb-2">
                      MT Renovierung & Innenausbau
                    </p>
                    <p className="text-gray-700">
                      {t('impressum.companyType')}
                    </p>
                  </div>
                </div>

                {/* Inhaber */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3 flex items-center gap-2">
                    <User className="h-5 w-5 text-orange-500" />
                    {t('impressum.owner')}
                  </h3>
                  <p className="text-gray-700 text-lg">
                    Maicol Talamo
                  </p>
                </div>

                {/* Anschrift */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-orange-500" />
                    {t('impressum.address')}
                  </h3>
                  <p className="text-gray-700">
                    Rieste Straße 4<br />
                    29553 Bienenbüttel<br />
                    {t('common.country')}
                  </p>
                </div>

                {/* Kontakt */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('impressum.contact')}
                  </h3>
                  <div className="space-y-3">
                    <p className="text-gray-700 flex items-center gap-3">
                      <Phone className="h-5 w-5 text-orange-500" />
                      <a href="tel:+4915207684235" className="hover:text-orange-500 transition-colors">
                        +49 152 07684235
                      </a>
                    </p>
                    <p className="text-gray-700 flex items-center gap-3">
                      <Mail className="h-5 w-5 text-orange-500" />
                      <a href="mailto:info@mtrenovierung.de" className="hover:text-orange-500 transition-colors">
                        info@mtrenovierung.de
                      </a>
                    </p>
                  </div>
                </div>

                {/* Verantwortlicher nach § 18 Abs. 2 MStV */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('impressum.responsible')}
                  </h3>
                  <p className="text-gray-700">
                    Maicol Talamo<br />
                    Rieste Straße 4<br />
                    29553 Bienenbüttel<br />
                    {t('common.country')}
                  </p>
                </div>

                {/* Verbraucherstreitbeilegung */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('impressum.consumerDispute.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('impressum.consumerDispute.description')}
                  </p>
                </div>

                {/* Gewerbeanmeldung */}
                <div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                    {t('impressum.businessRegistration.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('impressum.businessRegistration.description')}
                  </p>
                </div>

                {/* Hinweis für nicht-deutsche Versionen */}
                {lang !== 'de' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-gray-700 text-sm">
                      {t('impressum.legalNote')}
                    </p>
                  </div>
                )}
              </div>

              {/* Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  {t('impressum.moreInfo')}{" "}
                  <Link to={getPath('privacy')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('privacy.title')}
                  </Link>
                  ,{" "}
                  <Link to={getPath('cookies')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('cookiePolicy.title')}
                  </Link>
                  {" "}&{" "}
                  <Link to={getPath('disclaimer')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('disclaimer.title')}
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
